import React from 'react'
import styled from 'styled-components'

/**
 * A small markdown renderer for the ask widget.
 *
 * ⚠ SECURITY: this builds REACT ELEMENTS, never HTML strings, and
 * never touches dangerouslySetInnerHTML. The input is language-model
 * output over documents anyone can open a merge request against, so it
 * must be treated as untrusted: any raw HTML in the text renders as
 * literal characters rather than markup, which is exactly what we want
 * and is true by construction here.
 *
 * The subset is deliberately small — it only has to cover what the bot
 * actually emits: headings, lists, blockquotes, fenced code, bold,
 * italic, inline code and links. Adding a markdown dependency to a
 * Gatsby 2 / React 16 tree costs more than this does.
 */

const GITLAB_BLOB = 'https://gitlab.com/panterasbox/saxonberg/-/blob/master/'

/** docs/subsystems/parcel.md -> the file on GitLab's master branch. */
export const docUrl = (path) => GITLAB_BLOB + path.replace(/^\.?\//, '')

const H3 = styled.h3`
  margin: 0.4rem 0 0.1rem;
  font-size: 0.95rem;
  color: var(--chalk);
`
const H4 = styled.h4`
  margin: 0.3rem 0 0.1rem;
  font-size: 0.88rem;
  color: var(--chalk);
`
const P = styled.p`
  margin: 0;
`
const Ul = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
`
const Ol = styled.ol`
  margin: 0;
  padding-left: 1.3rem;
`
const Li = styled.li`
  margin: 0.15rem 0;
`
const Quote = styled.blockquote`
  margin: 0.2rem 0;
  padding: 0.1rem 0 0.1rem 0.7rem;
  border-left: 2px solid var(--accent);
  color: var(--chalk-dim);
`
const Pre = styled.pre`
  margin: 0.2rem 0;
  padding: 0.55rem 0.7rem;
  background: var(--ink);
  border: 1px solid var(--ink-rule);
  border-radius: 6px;
  overflow-x: auto;
  font-size: 0.8rem;
  line-height: 1.45;
`
const Code = styled.code`
  background: rgba(199, 214, 245, 0.12);
  border-radius: 3px;
  padding: 0.05rem 0.28rem;
  font-size: 0.85em;
  word-break: break-word;
`
const A = styled.a`
  color: var(--accent);
  text-decoration: underline;
  text-underline-offset: 2px;
  word-break: break-word;
  &:hover {
    color: var(--accent-strong);
  }
`

/**
 * Inline pass. Order matters: code spans are taken first so their
 * contents are never re-parsed, then links, then emphasis, and bare
 * doc paths last so a path already inside a link is left alone.
 */
const RULES = [
  { re: /`([^`]+)`/, render: (m, k) => <Code key={k}>{m[1]}</Code> },
  {
    re: /\[([^\]]+)\]\(([^)\s]+)\)/,
    render: (m, k) => (
      <A key={k} href={m[2]} target="_blank" rel="noopener noreferrer">
        {m[1]}
      </A>
    ),
  },
  { re: /\*\*([^*]+)\*\*/, render: (m, k) => <strong key={k}>{m[1]}</strong> },
  // No lookbehind here on purpose — it is ES2018, Babel cannot transpile
  // regex features, and older Safari would throw at parse time, taking
  // the whole bundle with it. Not needed anyway: "**bold**" cannot match
  // this pattern at index 0, and the bold rule wins on earliest index.
  { re: /\*([^*\n]+)\*/, render: (m, k) => <em key={k}>{m[1]}</em> },
  {
    // Bare documentation paths -> the canonical file on GitLab.
    re: /\b(docs\/[A-Za-z0-9._/-]+\.md)\b/,
    render: (m, k) => (
      <A key={k} href={docUrl(m[1])} target="_blank" rel="noopener noreferrer">
        {m[1]}
      </A>
    ),
  },
]

function inline(text, keyBase = 'i') {
  let earliest = null
  for (const rule of RULES) {
    const m = rule.re.exec(text)
    if (m && (!earliest || m.index < earliest.m.index)) earliest = { m, rule }
  }
  if (!earliest) return [text]

  const { m, rule } = earliest
  const before = text.slice(0, m.index)
  const after = text.slice(m.index + m[0].length)
  return [
    ...(before ? [before] : []),
    rule.render(m, `${keyBase}-${m.index}`),
    ...inline(after, `${keyBase}-${m.index}x`),
  ]
}

/** Block pass. Groups consecutive lines into paragraphs/lists/quotes. */
function blocks(src) {
  const lines = String(src).replace(/\r\n/g, '\n').split('\n')
  const out = []
  let i = 0
  let key = 0

  while (i < lines.length) {
    const line = lines[i]

    if (!line.trim()) {
      i++
      continue
    }

    // Fenced code — taken verbatim, no inline parsing inside.
    if (/^\s*```/.test(line)) {
      const body = []
      i++
      while (i < lines.length && !/^\s*```/.test(lines[i])) body.push(lines[i++])
      i++ // closing fence
      out.push(<Pre key={key++}>{body.join('\n')}</Pre>)
      continue
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line)
    if (heading) {
      const Tag = heading[1].length <= 2 ? H3 : H4
      out.push(<Tag key={key++}>{inline(heading[2], `h${key}`)}</Tag>)
      i++
      continue
    }

    if (/^\s*>\s?/.test(line)) {
      const body = []
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        body.push(lines[i++].replace(/^\s*>\s?/, ''))
      }
      out.push(<Quote key={key++}>{inline(body.join(' '), `q${key}`)}</Quote>)
      continue
    }

    if (/^\s*[-*+]\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        let item = lines[i++].replace(/^\s*[-*+]\s+/, '')
        // absorb wrapped continuation lines
        while (
          i < lines.length &&
          lines[i].trim() &&
          !/^\s*([-*+]|\d+\.)\s+/.test(lines[i]) &&
          !/^\s*(#|>|```)/.test(lines[i])
        ) {
          item += ' ' + lines[i++].trim()
        }
        items.push(item)
      }
      out.push(
        <Ul key={key++}>
          {items.map((t, n) => (
            <Li key={n}>{inline(t, `u${key}-${n}`)}</Li>
          ))}
        </Ul>
      )
      continue
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        let item = lines[i++].replace(/^\s*\d+\.\s+/, '')
        while (
          i < lines.length &&
          lines[i].trim() &&
          !/^\s*([-*+]|\d+\.)\s+/.test(lines[i]) &&
          !/^\s*(#|>|```)/.test(lines[i])
        ) {
          item += ' ' + lines[i++].trim()
        }
        items.push(item)
      }
      out.push(
        <Ol key={key++}>
          {items.map((t, n) => (
            <Li key={n}>{inline(t, `o${key}-${n}`)}</Li>
          ))}
        </Ol>
      )
      continue
    }

    // Paragraph: join wrapped lines until a blank or a new block starts.
    const para = []
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^\s*([-*+]|\d+\.)\s+/.test(lines[i]) &&
      !/^\s*(#|>|```)/.test(lines[i])
    ) {
      para.push(lines[i++].trim())
    }
    out.push(<P key={key++}>{inline(para.join(' '), `p${key}`)}</P>)
  }

  return out
}

const Markdown = ({ children }) => <>{blocks(children || '')}</>

export default Markdown
